// App.js
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './Header';
import SubHeader from './SubHeader';
import SidePanel from './SidePanel';
import TrustBar from './TrustBar';
import Footer from './Footer';
import '../StylesComponents/base.css';
import '../StylesComponents/design-system.css';
import '../StylesComponents/buttons.css';

import HomePage from '../Pages/HomePage';
import MensPage from '../Pages/MensPage';
import WomensPage from '../Pages/WomensPage';
import KidsPage from '../Pages/KidsPage';
import ShoesPage from '../Pages/ShoesPage';
import AccessoriesPage from '../Pages/AccessoriesPage';
import SignInPage from '../Pages/SignInPage';
import SignUpPage from '../Pages/SignUpPage';
import ForgotPasswordPage from '../Pages/ForgotPasswordPage';
import ResetPasswordPage from '../Pages/ResetPasswordPage';
import FeaturedPage from '../Pages/FeaturedPage';
import TrendingPage from '../Pages/TrendingPage';
import AllBrandsPage from '../Pages/AllBrandsPage';
import NewInPage from '../Pages/NewInPage';
import ProductPage from '../Pages/ProductPage';
import ProPage from '../Pages/ProPage';
import CustomerProfilePage from '../Pages/CustomerProfilePage';
import AdminRetailerDashboard from '../Pages/AdminRetailerDashboard';
import AdminCustomerDashboard from '../Pages/AdminCustomerDashboard';
import AboutPage from '../Pages/AboutPage';
import MediaKitPage from '../Pages/MediaKitPage';
import ContactUs from '../Pages/ContactUs';
import TermsOfService from '../Pages/TermsOfService';
import PrivacyPolicy from '../Pages/PrivacyPolicy';
import NotFoundPage from '../Pages/NotFoundPage';

import WishlistSidePanel from './WishlistSidePanel';
import MobileNavBar from './MobileNavBar';
import DropAlertPopup from './DropAlertPopup';
import DealScroller, { DEAL_SCROLLER_EXIT_KEY } from './DealScroller';
import ScrollToTop from '../utils/ScrollToTop';
import { WishlistProvider } from '../context/WishlistContext';
import '../StylesComponents/deal-scroller.css';

function readDealScrollerExited() {
  try {
    return sessionStorage.getItem(DEAL_SCROLLER_EXIT_KEY) === '1';
  } catch {
    return false;
  }
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser && storedUser !== 'undefined') {
        return JSON.parse(storedUser);
      }
    } catch (e) {
      console.error('Error parsing stored user:', e);
    }
    return null;
  });

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('user', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('user');
    }
  }, [currentUser]);

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const renderProfilePage = () => {
    if (!currentUser) return <SignInPage onLogin={(u) => setCurrentUser(u)} />;
    
    if (currentUser.accountType === 'admin_retailer') {
      return <AdminRetailerDashboard currentUser={currentUser} onLogout={handleLogout} />;
    }
    
    if (currentUser.accountType === 'admin_customer') {
      return <AdminCustomerDashboard currentUser={currentUser} onLogout={handleLogout} />;
    }
    
    return <CustomerProfilePage currentUser={currentUser} onLogout={handleLogout} setCurrentUser={setCurrentUser} />;
  };

  return (
    <Router>
      <ScrollToTop />
      <WishlistProvider currentUser={currentUser}>
        <AppContent 
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          currentUser={currentUser}
          onLogout={handleLogout}
          setCurrentUser={setCurrentUser}
          renderProfilePage={renderProfilePage}
        />
      </WishlistProvider>
    </Router>
  );
}

function AppContent({ 
  menuOpen, setMenuOpen, searchQuery, setSearchQuery, 
  currentUser, onLogout, setCurrentUser, renderProfilePage,
}) {
  const location = useLocation();
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollerOpen, setScrollerOpen] = useState(() => (
    typeof window !== 'undefined'
      && window.location.pathname === '/'
      && !readDealScrollerExited()
  ));
  const [isExiting, setIsExiting] = useState(false);
  const scrollerPathRef = useRef(location.pathname);
  const metaPageViewReady = useRef(false);

  // Base snippet already fires the first PageView. Track later route changes
  // so a single-page navigation still counts in Meta.
  useEffect(() => {
    if (!metaPageViewReady.current) {
      metaPageViewReady.current = true;
      return;
    }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const controlNavbar = () => {
      if (typeof window !== 'undefined') {
        const currentScrollY = window.scrollY;
        const isMobile = window.innerWidth <= 768;
        
        if (!isMobile) {
          setIsNavVisible(true);
          return;
        }

        if (currentScrollY < 10) {
          setIsNavVisible(true);
        } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsNavVisible(false);
        } else {
          setIsNavVisible(true);
        }
        
        setLastScrollY(currentScrollY);
      }
    };

    window.addEventListener('scroll', controlNavbar);
    return () => window.removeEventListener('scroll', controlNavbar);
  }, [lastScrollY]);

  useEffect(() => {
    if (!scrollerOpen) {
      document.documentElement.classList.remove('deal-scroller-active');
      document.body.classList.remove('deal-scroller-active');
      return undefined;
    }

    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('deal-scroller-active');
    document.body.classList.add('deal-scroller-active');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    const preventBackgroundScroll = (event) => {
      const target = event.target;
      if (target && typeof target.closest === 'function' && target.closest('.deal-scroller-rail')) {
        return;
      }
      event.preventDefault();
    };

    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });

    return () => {
      document.removeEventListener('touchmove', preventBackgroundScroll);
      document.removeEventListener('wheel', preventBackgroundScroll);
      document.documentElement.classList.remove('deal-scroller-active');
      document.body.classList.remove('deal-scroller-active');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [scrollerOpen]);

  // Auto-open deal feed when landing on home for this session.
  useEffect(() => {
    if (location.pathname === '/' && !readDealScrollerExited() && !scrollerOpen) {
      setScrollerOpen(true);
      setIsExiting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // If the user navigates while the feed is open (e.g. brand tap), close it.
  useEffect(() => {
    if (scrollerOpen && scrollerPathRef.current !== location.pathname) {
      setScrollerOpen(false);
      setIsExiting(false);
      try {
        sessionStorage.setItem(DEAL_SCROLLER_EXIT_KEY, '1');
      } catch (_) {}
      document.body.classList.remove('deal-scroller-active');
      document.documentElement.classList.remove('deal-scroller-active');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
    }
    scrollerPathRef.current = location.pathname;
  }, [location.pathname, scrollerOpen]);

  const handleExitScroller = () => {
    if (isExiting) return;
    setIsExiting(true);
    try {
      sessionStorage.setItem(DEAL_SCROLLER_EXIT_KEY, '1');
    } catch (_) {}
    window.setTimeout(() => {
      setScrollerOpen(false);
      setIsExiting(false);
    }, 380);
  };

  const handleOpenScroller = () => {
    try {
      sessionStorage.removeItem(DEAL_SCROLLER_EXIT_KEY);
    } catch (_) {}
    setIsExiting(false);
    setScrollerOpen(true);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="App">
      <TrustBar />
      <Header
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        currentUser={currentUser}
        onLogout={onLogout}
        isVisible={isNavVisible}
      />
      <SubHeader 
        currentUser={currentUser} 
        toggleWishlist={() => setIsWishlistOpen(!isWishlistOpen)}
        isVisible={isNavVisible}
      />

      <SidePanel
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        currentUser={currentUser}
        onLogout={onLogout}
      />

      <WishlistSidePanel 
        isOpen={isWishlistOpen} 
        onClose={() => setIsWishlistOpen(false)} 
        currentUser={currentUser} 
      />

      {scrollerOpen && (
        <DealScroller
          currentUser={currentUser}
          onLogin={(u) => setCurrentUser(u)}
          onExit={handleExitScroller}
          isExiting={isExiting}
        />
      )}

      <button
        type="button"
        className={`deal-scroller-entry${!isNavVisible ? ' is-hidden' : ''}`}
        onClick={handleOpenScroller}
        aria-label="Enter deal feed"
      >
        <span className="deal-scroller-entry-swipe" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        <span className="deal-scroller-entry-label">Deal Feed</span>
        <span className="deal-scroller-entry-action">
          Swipe deals
          <span className="deal-scroller-entry-arrow" aria-hidden="true" />
        </span>
      </button>

      <main>
        <Routes>
          <Route
            path="/"
            element={<HomePage currentUser={currentUser} />}
          />
            <Route path="/signin" element={<SignInPage onLogin={(u) => setCurrentUser(u)} />} />
            <Route path="/signup" element={<SignUpPage onLogin={(u) => setCurrentUser(u)} />} />
           <Route path="/forgot-password" element={<ForgotPasswordPage />} />
           <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/featured" element={<FeaturedPage currentUser={currentUser} />} />
           <Route path="/trending" element={<TrendingPage currentUser={currentUser} />} />
           <Route path="/all-brands" element={<AllBrandsPage currentUser={currentUser} />} />
           <Route path="/new-in" element={<NewInPage currentUser={currentUser} />} />
           <Route path="/mens" element={<MensPage currentUser={currentUser} />} />
           <Route path="/womens" element={<WomensPage currentUser={currentUser} />} />
           <Route path="/kids" element={<KidsPage currentUser={currentUser} />} />
            <Route path="/shoes" element={<ShoesPage currentUser={currentUser} />} />
            <Route path="/accessories" element={<AccessoriesPage currentUser={currentUser} />} />
            <Route path="/product/:id" element={<ProductPage currentUser={currentUser} />} />
            <Route path="/pro" element={<ProPage currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
            <Route path="/profile" element={renderProfilePage()} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/manifest/rakuten-media-kit-2026" element={<MediaKitPage />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="*" element={<NotFoundPage />} />
         </Routes>
      </main>

      <Footer />
      <DropAlertPopup />
      <MobileNavBar 
        toggleWishlist={() => setIsWishlistOpen(!isWishlistOpen)} 
        setMenuOpen={setMenuOpen}
      />
    </div>
  );
}

export default App;
